/**
 * Mo's welcome: the head of every conversation in AI Studio. A bold greeting, Mo's
 * line "let me take a quick look", a compact briefing card (live chat with the online
 * switch and the team, conversations with a curve over 7 / 14 / 30 days, success score;
 * each column one value, one note and one action; the AI credits are in the sidebar), then the question. The
 * lines are typed out one after another and the card comes in once its data is there;
 * every line and the card keep their space from the start, so nothing below them moves.
 * onDone tells the start page that the welcome is complete, its question written (the
 * input and the questions under it appear then). Built from live data in the portal (GET /overview, the rich bot
 * list, the live roster), no model call. Score data and step targets are the classic
 * overview's (pages/overviewCards.js).
 * Typed out and counted up only on a fresh start page and at most every 30 minutes (`fresh`); otherwise it
 * stands at once with the data the page session already has (studioData), refreshed in the background. The
 * icon next to Mo's line reloads its figures, the score, the team and the credits.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faChevronLeft, faChevronRight, faRotateRight } from '@fortawesome/free-solid-svg-icons';
import AgentAvailability from '../AgentAvailability.js';
import SuccessScoreModal, { calculateSuccessScore, openSuccessSteps } from '../SuccessScoreModal.js';
import { useSelectedBot } from '../../contexts/SelectedBotContext.js';
import useUpgrade from '../../hooks/useUpgrade.js';
import { refreshAiCredits } from '../../hooks/useAiCredits';
import { formatDayForUser, SCORE_TARGETS, useCountUp } from '../../pages/overviewCards';
import { loadOverview, loadRichBots, useRichBot } from './studioData';
import { PANEL_TAB_EVENT } from './AgentPanel';

const WAIT_MS = 2200; // the card comes at the latest after this, data or not
const CLOSING_MS = 650; // the question follows the card
const RANGES = [7, 14, 30];
const RANGE_KEY = 'agentStudio.welcomeRange';

function readFirstName() {
  try { return (localStorage.getItem('first_name') || '').trim(); } catch (_) { return ''; }
}
function readRange() {
  try {
    const n = Number(localStorage.getItem(RANGE_KEY));
    return RANGES.includes(n) ? n : RANGES[0];
  } catch (_) { return RANGES[0]; }
}
function reducedMotion() {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
}
function personName(a) {
  return a.first_name || (a.email || '').split('@')[0] || `#${a.user_id}`;
}
/** The hour in the timezone of the user's account (Account settings; the portal keeps it in localStorage),
 *  the browser's clock only when there is none or it is unknown. */
function userHour(now) {
  try {
    const tz = localStorage.getItem('timezone');
    if (tz) {
      const h = Number(new Intl.DateTimeFormat('en-US', { hour: 'numeric', hourCycle: 'h23', timeZone: tz }).format(now));
      if (Number.isFinite(h)) return h % 24;
    }
  } catch (_) { /* no storage or an unknown timezone */ }
  return now.getHours();
}
/** morning 5:00-11:59, afternoon 12:00-17:59, evening 18:00-4:59 (11:48 is not "Good afternoon") */
export function dayPart(now = new Date()) {
  const hour = userHour(now);
  return hour >= 5 && hour < 12 ? 'morning' : hour >= 12 && hour < 18 ? 'day' : 'evening';
}
/** red / amber / green / brand like the classic overview's ring (overviewCards scoreColor) */
function scoreTone(pct) {
  if (pct >= 100) return 'is-full';
  if (pct >= 70) return 'is-good';
  if (pct >= 40) return 'is-mid';
  return 'is-low';
}

/** a value that grows from 0 once mounted (the bar, the ring); `calm`: at its value at once */
function useGrow(target, calm = false) {
  const [v, setV] = useState(reducedMotion() || calm ? target : 0);
  useEffect(() => {
    const id = requestAnimationFrame(() => setV(target));
    return () => cancelAnimationFrame(id);
  }, [target]);
  return v;
}

/**
 * A line typed out once `run` is true, `ms` in total. Until it is complete, the full text
 * stays in place invisibly, so the line has its final size from the start.
 */
function Typed({ as: Tag, className, text, run, ms, onDone, instant = false }) {
  const [n, setN] = useState(() => (reducedMotion() || instant ? text.length : 0));
  const done = n >= text.length;
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  useEffect(() => {
    if (!run || done) return undefined;
    const step = Math.max(12, ms / Math.max(1, text.length));
    let t0 = null;
    let base = 0;
    // clock based: a throttled background tab catches up instead of typing slower
    const id = setInterval(() => {
      setN((cur) => {
        if (t0 === null) { t0 = performance.now(); base = cur; }
        return Math.min(text.length, base + Math.floor((performance.now() - t0) / step));
      });
    }, 16);
    return () => clearInterval(id);
  }, [run, done, text, ms]);
  useEffect(() => { if (run && done) doneRef.current?.(); }, [run, done]);

  if (done) return <Tag className={className}>{text}</Tag>;
  return (
    <Tag className={`${className} as-tw`}>
      <span className="as-tw-ghost" aria-hidden="true">{text}</span>
      {run && (
        <span className="as-tw-text" aria-hidden="true">
          {text.slice(0, n)}<i className="as-tw-caret" />
        </span>
      )}
    </Tag>
  );
}

/** one column of the card: label (+ something on the right), value, note, action */
function Stat({ step, label, side = null, value, note, action, onAction }) {
  return (
    <div className="as-wb-stat" style={{ '--d': step }}>
      <div className="as-wb-label"><span>{label}</span>{side}</div>
      <div className="as-wb-value">{value}</div>
      <div className="as-wb-note" title={note || undefined}>{note}</div>
      <button type="button" className="as-wb-action" onClick={onAction}>
        {action} <FontAwesomeIcon icon={faArrowRight} />
      </button>
    </div>
  );
}

/** Narrow screens (Robert, 18.10.2026): the card's three columns stay side by side and are swiped through, each
 *  snapping into place (CSS scroll-snap, agentStudio.css); a round arrow at mid height shows that there is more to the
 *  left or right and moves one column. On wide screens nothing overflows and the arrows stay hidden. */
function CardScroller({ cardKey, children }) {
  const { t } = useTranslation();
  const wrap = useRef(null);
  const [more, setMore] = useState({ left: false, right: false });
  const card = () => wrap.current?.querySelector('.as-wb-card');
  useLayoutEffect(() => {
    const el = card();
    if (!el) return undefined;
    const check = () => {
      const left = el.scrollLeft > 2;
      const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
      setMore((m) => (m.left === left && m.right === right ? m : { left, right }));
    };
    check();
    el.addEventListener('scroll', check, { passive: true });
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(check);
      ro.observe(el);
    }
    return () => { el.removeEventListener('scroll', check); ro?.disconnect(); };
  }, [cardKey]);
  const go = (dir) => {
    const el = card();
    const col = el?.querySelector('.as-wb-stat');
    if (el && col) el.scrollBy({ left: dir * col.offsetWidth, behavior: 'smooth' });
  };
  const arrow = (side, dir, icon, label) => (
    <button type="button" className={`as-wb-arrow is-${side} ${more[side] ? 'is-shown' : ''}`} onClick={() => go(dir)}
      aria-label={label} aria-hidden={!more[side]} tabIndex={more[side] ? 0 : -1}>
      <FontAwesomeIcon icon={icon} />
    </button>
  );
  return (
    <div className="as-wb-scroller" ref={wrap}>
      {children}
      {arrow('left', -1, faChevronLeft, t('agent_studio_welcome_prev'))}
      {arrow('right', 1, faChevronRight, t('agent_studio_welcome_next'))}
    </div>
  );
}

/** the card's shape while Mo looks: same columns and lines, shimmering */
function Skeleton({ visible }) {
  const { t } = useTranslation();
  const bar = (w) => <span className="as-wb-sk" style={{ width: w }}>&nbsp;</span>;
  return (
    <div className={`as-wb-card is-skeleton ${visible ? '' : 'is-hidden'}`}
      role={visible ? 'status' : undefined} aria-label={visible ? t('agent_studio_welcome_reading') : undefined}>
      {[0, 1, 2].map((i) => (
        <div className="as-wb-stat" key={i} aria-hidden="true">
          <div className="as-wb-label">{bar(i === 1 ? 74 : 62)}</div>
          <div className="as-wb-value"><span className="as-wb-big">{bar(54)}</span></div>
          <div className="as-wb-note">{bar(118)}</div>
          <div className="as-wb-action is-sk">{bar(72)}</div>
        </div>
      ))}
    </div>
  );
}

/** the conversations of the range as a smooth curve, as wide as the column allows; pointing at it
 *  (mouse or finger) shows that day in the user's date format and its number of conversations */
function Spark({ points }) {
  const { t } = useTranslation();
  const box = useRef(null);
  const [w, setW] = useState(160);
  const [at, setAt] = useState(null);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const measure = () => setW(Math.max(40, Math.round(el.getBoundingClientRect().width)));
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const h = 30;
  const max = Math.max(1, ...points.map((p) => p.count));
  const xy = points.map((p, i) => [points.length > 1 ? (i / (points.length - 1)) * w : w, h - 3 - (p.count / max) * (h - 7)]);
  let d = `M${xy[0][0]},${xy[0][1]}`;
  for (let i = 1; i < xy.length; i += 1) {
    const [x0, y0] = xy[i - 1];
    const [x1, y1] = xy[i];
    const cx = (x0 + x1) / 2;
    d += ` C${cx},${y0} ${cx},${y1} ${x1},${y1}`;
  }
  const point = (e) => {
    const r = box.current.getBoundingClientRect();
    const f = r.width > 0 ? (e.clientX - r.left) / r.width : 1;
    setAt(Math.max(0, Math.min(points.length - 1, Math.round(f * (points.length - 1)))));
  };
  const p = at == null ? null : points[at];
  const x = at == null ? 0 : xy[at][0];
  return (
    <div className="as-wb-spark" ref={box} onPointerMove={point} onPointerDown={point} onPointerLeave={() => setAt(null)}>
      <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} aria-hidden="true">
        <defs>
          <linearGradient id="asWbSpark" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#5e1bff" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#5e1bff" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={`${d} L${w},${h} L0,${h} Z`} fill="url(#asWbSpark)" className="as-wb-spark-fill" />
        <path d={d} fill="none" stroke="#5e1bff" strokeWidth="1.75" strokeLinecap="round" pathLength="1"
          className="as-wb-spark-line" />
        {p && <circle cx={x} cy={xy[at][1]} r="3.5" className="as-wb-spark-dot" />}
      </svg>
      {p && (
        <div className={`as-wb-tip ${x < 56 ? 'is-start' : x > w - 56 ? 'is-end' : ''}`} style={{ left: x }} role="tooltip">
          <span>{formatDayForUser(p.day)}</span>
          <b>{t(p.count === 1 ? 'agent_studio_welcome_conv_point_one' : 'agent_studio_welcome_conv_point', { count: p.count })}</b>
        </div>
      )}
    </div>
  );
}

function MiniRing({ pct, calm }) {
  const r = 11;
  const c = 2 * Math.PI * r;
  const grown = useGrow(pct, calm);
  return (
    <svg className={`as-wb-ring ${scoreTone(pct)}`} viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
      <circle cx="14" cy="14" r={r} className="as-wb-ring-track" />
      <circle cx="14" cy="14" r={r} className="as-wb-ring-value" strokeDasharray={c}
        strokeDashoffset={c * (1 - Math.min(100, grown) / 100)} transform="rotate(-90 14 14)" />
    </svg>
  );
}

function Briefing({ bot, richBot, figures, live, range, onRange, availRef, onScore, calm }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language || 'en';
  const num = (n) => new Intl.NumberFormat(lang).format(Math.round(n));
  const pct = (n) => new Intl.NumberFormat(lang, { style: 'percent', maximumFractionDigits: 0 }).format(n / 100);
  const list = (names) => new Intl.ListFormat(lang, { style: 'long', type: 'conjunction' }).format(names);

  // live chat
  const others = live.roster.filter((a) => !a.is_self && a.online).map(personName);
  const liveNote = !live.ready ? '' : others.length
    ? t(others.length === 1 ? 'agent_studio_welcome_live_one' : 'agent_studio_welcome_live_many', { names: list(others) })
    : live.online ? t('agent_studio_welcome_live_only_you') : t('agent_studio_welcome_live_none');

  // conversations
  const r = figures?.ranges?.[String(range)];
  const delta = r ? r.conversations - r.previous : 0;
  const shownCount = useCountUp(r ? r.conversations : 0, 700, calm);
  const series = useMemo(() => {
    const byDay = Object.fromEntries((figures?.daily || []).map((d) => [d.day, d.conversations]));
    const today = new Date();
    const out = [];
    for (let i = range - 1; i >= 0; i -= 1) {
      const day = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i))
        .toISOString().slice(0, 10);
      out.push({ day, count: byDay[day] || 0 });
    }
    return out;
  }, [figures, range]);

  // success score
  const score = useMemo(
    () => (richBot ? calculateSuccessScore(richBot, richBot.success_score ?? {}) : null),
    [richBot],
  );
  const open = score ? score.total - score.completed : 0;
  const shownScore = useCountUp(score ? score.percentage : 0, 800, calm);

  return (
    <div className={`as-wb-card ${calm ? 'is-calm' : ''}`}>
      <Stat step={0} label={t('ov_live_chat_title')}
        value={<span className="as-wb-live"><AgentAvailability ref={availRef} bot={bot} onState={live.set} /></span>}
        note={liveNote} action={t('agent_studio_welcome_team')} onAction={() => availRef.current?.openRoster()} />

      <Stat step={1} label={t('agent_studio_welcome_conv_title')}
        side={(
          <span className="as-wb-seg" role="group" aria-label={t('ov_activity_range_aria')}>
            {RANGES.map((n) => (
              <button key={n} type="button" className={range === n ? 'is-active' : ''} aria-pressed={range === n}
                onClick={() => onRange(n)}>{t('ov_range_days_short', { n })}</button>
            ))}
          </span>
        )}
        value={(
          <>
            <span className="as-wb-big">{r ? num(shownCount) : '–'}</span>
            {r && delta !== 0 && (
              <span className={`as-wb-delta ${delta > 0 ? 'is-up' : ''}`} title={t('agent_studio_welcome_conv_delta', { days: range })}>
                {delta > 0 ? `+${num(delta)}` : `−${num(-delta)}`}
              </span>
            )}
            <Spark key={range} points={series} />
          </>
        )}
        note={r && r.leads > 0
          ? t(r.leads === 1 ? 'agent_studio_welcome_conv_leads_one' : 'agent_studio_welcome_conv_leads', { count: r.leads })
          : t('agent_studio_welcome_conv_days', { days: range })}
        action={t('nav_insights')} onAction={() => navigate('/activity/insights')} />

      <Stat step={2} label={t('ov_success_score_title')}
        value={score ? (
          <>
            <MiniRing pct={score.percentage} calm={calm} />
            <span className="as-wb-big">{pct(shownScore)}</span>
          </>
        ) : <span className="as-wb-big">–</span>}
        note={!score ? '' : open === 0 ? t('agent_studio_welcome_score_done')
          : t(open === 1 ? 'agent_studio_welcome_score_open_one' : 'agent_studio_welcome_score_open_n', { count: open })}
        action={t('agent_studio_welcome_improve')} onAction={onScore} />
    </div>
  );
}

// the welcome is typed out at most this often; in between it stands at once
const ANIMATE_EVERY_MS = 30 * 60 * 1000;
const ANIMATED_KEY = 'agentStudio.welcomeAnimatedAt';
function animationDue() {
  try {
    const last = Number(localStorage.getItem(ANIMATED_KEY) || 0);
    if (Date.now() - last < ANIMATE_EVERY_MS) return false;
    localStorage.setItem(ANIMATED_KEY, String(Date.now()));
  } catch (_) { /* storage unavailable: type it */ }
  return true;
}

export default function WelcomeMessage({ hasAgent, figures, onAsk, onDone, fresh = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedBot, selectedBotId, refreshBots } = useSelectedBot();
  const { handleUpgradeClick, UpgradeModal } = useUpgrade({ onComplete: refreshBots });
  const richBot = useRichBot(selectedBotId);
  const [liveState, setLiveState] = useState({ roster: [], online: false, ready: false });
  const [range, setRange] = useState(readRange);
  const [scoreOpen, setScoreOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // typed out only on a fresh start page, at most every 30 minutes; otherwise it stands at once
  const [animate] = useState(() => fresh && !reducedMotion() && animationDue());
  // 0 greeting, 1 Mo's line, 2 waiting for the figures, 3 the question, 4 complete
  const [step, setStep] = useState(() => (animate ? 0 : 4));
  const [late, setLate] = useState(false);
  const availRef = useRef(null);
  const bot = richBot || selectedBot;
  const steps = useMemo(() => (richBot ? openSuccessSteps(richBot, richBot.success_score ?? {}) : []), [richBot]);
  const ready = !!figures && !!richBot;
  const showCard = !!bot && (ready || late) && step >= 2;

  useEffect(() => {
    const id = setTimeout(() => setLate(true), WAIT_MS);
    return () => clearTimeout(id);
  }, []);
  useEffect(() => {
    if (step !== 2 || !showCard) return undefined;
    const id = setTimeout(() => setStep(3), CLOSING_MS);
    return () => clearTimeout(id);
  }, [step, showCard]);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  useEffect(() => { if (step === 4) doneRef.current?.(); }, [step]);

  const pickRange = (n) => {
    setRange(n);
    try { localStorage.setItem(RANGE_KEY, String(n)); } catch (_) { /* storage unavailable */ }
  };
  const helpFix = () => {
    setScoreOpen(false);
    onAsk(t('agent_studio_score_fix_text', { items: steps.map((id) => t(`agent_studio_score_step_${id}`)).join(', ') }));
  };
  const refresh = async () => {
    if (refreshing || !selectedBotId) return;
    setRefreshing(true);
    await Promise.all([
      loadOverview(selectedBotId), loadRichBots(), refreshAiCredits(selectedBotId),
      Promise.resolve(availRef.current?.refresh?.()),
    ]).catch(() => {});
    setRefreshing(false);
  };
  const name = readFirstName();
  const hello = t(`agent_studio_hello_${dayPart()}`);
  const question = hasAgent ? t('agent_studio_welcome_question_agent') : t('agent_studio_welcome_question_new');

  return (
    <div className="as-welcome">
      <Typed as="h1" className="as-wl-hello" text={name ? `${hello}, ${name}` : hello} run ms={520} instant={!animate}
        onDone={() => setStep((s) => Math.max(s, 1))} />
      <div className="as-wl-line-row">
        <Typed as="p" className="as-wl-line" text={t('agent_studio_welcome_look')} run={step >= 1} ms={1000} instant={!animate}
          onDone={() => setStep((s) => Math.max(s, 2))} />
        <button type="button" className={`as-wl-refresh ${showCard ? '' : 'is-hidden'}`} onClick={refresh}
          disabled={refreshing || !showCard} title={t('agent_studio_welcome_refresh')} aria-label={t('agent_studio_welcome_refresh')}>
          <FontAwesomeIcon icon={faRotateRight} spin={refreshing} />
        </button>
      </div>

      <CardScroller cardKey={showCard ? 'card' : 'skeleton'}>
        {showCard ? (
          <Briefing bot={bot} richBot={richBot} figures={figures} live={{ ...liveState, set: setLiveState }}
            range={range} onRange={pickRange} availRef={availRef} calm={!animate}
            onScore={() => richBot && setScoreOpen(true)} />
        ) : <Skeleton visible={step >= 1} />}
      </CardScroller>

      <Typed as="p" className="as-wl-question" text={question} run={step >= 3} ms={Math.min(1600, question.length * 26)}
        instant={!animate} onDone={() => setStep(4)} />

      {scoreOpen && richBot && (
        <SuccessScoreModal
          open={scoreOpen}
          onClose={() => setScoreOpen(false)}
          bot={richBot}
          successData={richBot.success_score || {}}
          onSettingsClick={(b, tab) => { setScoreOpen(false); navigate(SCORE_TARGETS[tab] || SCORE_TARGETS.skills); }}
          onOpenPanel={(tab) => { setScoreOpen(false); window.dispatchEvent(new CustomEvent(PANEL_TAB_EVENT, { detail: { tab } })); }}
          onUpgradeClick={handleUpgradeClick}
          onHelpFix={helpFix}
        />
      )}
      {UpgradeModal}
    </div>
  );
}
